import { forwardRef, type PropsWithChildren, useState } from 'react';
import { mergeRefs } from 'react-merge-refs';
import styled, { css } from 'styled-components';

import { isElementScrollable } from '#@/shared/utils/dom';
import { sizes } from '#@/shared/utils/styles';

/**
 * As of Feb 2025, Firefox contains a bug where the scrollbar does not count
 * towards the intrinsic width of a scrollable element. (See:
 * https://bugzilla.mozilla.org/show_bug.cgi?id=764076.) Basically, this means
 * that adding a vertical scrollbar to an element will cover some of the
 * content, possibly causing it to overflow horizontally. Blink browsers and
 * Safari don't have this bug — they increase the size of the element (if
 * possible) when a scrollbar gets added.
 *
 * A workaround to this is to use `scrollbar-gutter: stable`, which reserves
 * space for a scrollbar whether the element is scrollable or not. Using
 * Javascript, we should be able to only enable this when the element actually
 * is scrollable, mitigating any side effects.
 */
const firefoxHack = css`
  scrollbar-gutter: stable;
`;

const Wrapper = styled.div<{ $isScrollable: boolean }>`
  padding: ${sizes.dropdownCardContentPadding}px;

  ${(props) =>
    props.$isScrollable &&
    css`
      ${firefoxHack};
      padding-right: ${sizes.dropdownCardContentPaddingReduced}px;
    `}
`;

const DropdownCardContent = forwardRef<HTMLElement, PropsWithChildren>((props, ref) => {
  const [element, setElement] = useState<HTMLElement | null>(null);

  const isScrollable = Boolean(element && isElementScrollable(element));

  return <Wrapper ref={mergeRefs([ref, setElement])} {...props} $isScrollable={isScrollable} />;
});
DropdownCardContent.displayName = 'DropdownCardContent';

export default DropdownCardContent;
